import { DataGridPro, GridRowClassNameParams, GridRowId, GridRowParams, GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { getDriverColumnDefinitions, getDriverMobileColumnDefinitions } from './columns';
import { useMediaQuery, useTheme } from '@mui/material';
import { applyThemeGridSxOverrides } from '@/Infrastructure/Theme/AppThemeOverrides';
import { isDriverSelectable } from './types';
import { ExtendedDriver } from '@hooks/useDriverTractors.types';
import { useMemo } from 'react';
import AssignDriverGridToolbar from '../AssignDriverToolbar';
import { GenericOverlay } from '../GenericNoRowsOverlay';
import { DriverDataGridProps } from '../types';

import styles from './DataGrid.Driver.module.css';

/**
 * MUI Data Grid Pro component for selecting a single Driver and Tractor combination
 */
export default function Grid({ displayCurrentTractor, legRequiresHazmat, disableRowSelectValidation, ...props }: DriverDataGridProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  function onSingleRowSelect(rowSelectionModel: GridRowSelectionModel) {
    if (rowSelectionModel.ids.size > 0) {
      const [, selectedRowId] = rowSelectionModel.ids.entries().next().value as [never, GridRowId];

      const selectedRow = props.rows.find((driver) => driver.id === selectedRowId);

      if (selectedRow) {
        /** Execute the callback */
        props.onRowSelect(selectedRow);
      }
    }
  }

  function isRowSelectable({ row }: GridRowParams<ExtendedDriver>) {
    return disableRowSelectValidation
      ? true
      : isDriverSelectable(row, legRequiresHazmat ?? false, Boolean(displayCurrentTractor)).isSelectable;
  }

  function getRowStyles({ row }: GridRowClassNameParams<ExtendedDriver>) {
    if (disableRowSelectValidation) return '';
    return !isDriverSelectable(row, legRequiresHazmat ?? false, Boolean(displayCurrentTractor)).isSelectable ? styles['disabled-row'] : '';
  }

  const driverColumns = useMemo(
    () => getDriverColumnDefinitions(Boolean(displayCurrentTractor), legRequiresHazmat ?? false),
    [displayCurrentTractor, legRequiresHazmat]
  );

  const mobileDriverColumns = useMemo(
    () => getDriverMobileColumnDefinitions(Boolean(displayCurrentTractor), legRequiresHazmat ?? false),
    [displayCurrentTractor, legRequiresHazmat]
  );

  return (
    <DataGridPro
      apiRef={props.gridApiRef}
      rows={props.rows}
      columns={isMobile ? mobileDriverColumns : driverColumns}
      loading={props.isLoading}
      onRowSelectionModelChange={onSingleRowSelect}
      isRowSelectable={isRowSelectable}
      getRowClassName={getRowStyles}
      rowHeight={isMobile ? 90 : 50}
      columnHeaderHeight={45}
      resizeThrottleMs={0}
      slots={{
        toolbar: AssignDriverGridToolbar,
        noRowsOverlay: () => <GenericOverlay overlayText='No Drivers Found' />,
        noResultsOverlay: () => <GenericOverlay overlayText='No Driver Results' />
      }}
      slotProps={{
        loadingOverlay: {
          variant: 'skeleton'
        },
        toolbar: {
          driverToolbarOverrides: {
            slickRef: props.slickRef,
            defaultFilterModel: props.defaultFilterModel
          }
        }
      }}
      columnVisibilityModel={{
        tractorId: displayCurrentTractor ?? false,
        hazmatExpirationDate: !isMobile,
        isAssignedJob: false,
        isOnLastJob: false
      }}
      sx={{
        ...applyThemeGridSxOverrides(),
        '& .MuiDataGrid-columnHeaders': isMobile
          ? {
              display: 'none'
            }
          : {}
      }}
    />
  );
}
